import { BasicFieldInputs } from '../components/BasicFieldInputs';
import { InputListItem } from '../components/InputListItem';
import { PickerOptionsConfig } from '../components/PickerOptionsConfig';
import { ImagePickerConfig } from '../components/ImagePickerConfig';
import { Button, Checkbox, Select, Stack, Flex } from '@mantine/core';
import { CustomInput } from '../../CustomInput';
import { useIntl } from 'react-intl';
import { toInputName } from '../utils';
import { notifications } from '@mantine/notifications';
import { InfiniteListFieldData, InputType, ImageFile } from './types';

interface InfiniteListFieldProps {
  field: InfiniteListFieldData;
  setField: (field: InfiniteListFieldData) => void;
}

export const InfiniteListField = ({
  field,
  setField,
}: InfiniteListFieldProps) => {
  const intl = useIntl();

  const handlePhotosAdded = (images: ImageFile[], folderPath: string) => {
    setField({
      ...field,
      tempAvailableImages: [...(field.tempAvailableImages || []), ...images],
      tempFolderPath: folderPath,
    });
  };

  const handleRemoveInput = (index: number) => {
    if (!field.inputs) return;

    const removedInput = field.inputs[index];
    const updatedInputs = field.inputs.filter((_, i) => i !== index);

    // Remove the field from all existing values
    const updatedValues = (field.value || []).map((item: any) => {
      const { [removedInput.name]: removed, ...rest } = item;
      return rest;
    });

    // Remove from diacritics and autoCorrect arrays if present
    const updatedDiacritics = (field.diacritics || []).filter(
      (d) => d !== removedInput.name
    );
    const updatedAutoCorrect = (field.autoCorrect || []).filter(
      (a) => a !== removedInput.name
    );

    setField({
      ...field,
      inputs: updatedInputs,
      value: updatedValues,
      diacritics: updatedDiacritics,
      autoCorrect: updatedAutoCorrect,
    });
  };

  const handleUpdateInput = (index: number, updatedInput: any) => {
    if (!field.inputs) return;

    const updatedInputs = [...field.inputs];
    updatedInputs[index] = updatedInput;

    setField({
      ...field,
      inputs: updatedInputs,
    });
  };

  const handleDiacriticsChange = (inputName: string, checked: boolean) => {
    const currentDiacritics = field.diacritics || [];
    if (checked) {
      // Add to diacritics if not already present
      if (!currentDiacritics.includes(inputName)) {
        setField({
          ...field,
          diacritics: [...currentDiacritics, inputName],
        });
      }
    } else {
      // Remove from diacritics
      setField({
        ...field,
        diacritics: currentDiacritics.filter((name) => name !== inputName),
      });
    }
  };

  const handleAutoCorrectChange = (inputName: string, checked: boolean) => {
    const currentAutoCorrect = field.autoCorrect || [];
    if (checked) {
      // Add to autoCorrect if not already present
      if (!currentAutoCorrect.includes(inputName)) {
        setField({
          ...field,
          autoCorrect: [...currentAutoCorrect, inputName],
        });
      }
    } else {
      // Remove from autoCorrect
      setField({
        ...field,
        autoCorrect: currentAutoCorrect.filter((name) => name !== inputName),
      });
    }
  };

  const handleAddInput = () => {
    if (!field.tempInputType || !field.tempInputName) {
      return;
    }

    // Check for duplicates
    const isDuplicate = (field.inputs || []).some(
      (input) => input.name === field.tempInputName
    );

    if (isDuplicate) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage(
          { id: 'inputNameAlreadyExists' },
          { name: field.tempInputName }
        ),
        color: 'red',
      });
      return;
    }

    // Determine default value based on type
    const defaultValue =
      field.tempInputType === 'imagePicker' ||
      field.tempInputType === 'checkboxGroup'
        ? []
        : field.tempInputType === 'signature' ||
            field.tempInputType === 'datepicker'
          ? null
          : '';

    // Add the new field to all existing values
    const updatedValues = (field.value || []).map((item: any) => ({
      ...item,
      [field.tempInputName as string]: defaultValue,
    }));

    // Build the new input object
    const newInput: any = {
      type: field.tempInputType,
      name: field.tempInputName,
      label: field.tempInputLabel || '',
    };

    if (
      field.tempInputType === 'picker' ||
      field.tempInputType === 'checkboxGroup'
    ) {
      newInput.items = field.tempInputValues || [];
    }

    if (field.tempInputType === 'imagePicker') {
      newInput.availableImages = field.tempAvailableImages || [];
      newInput.folderPath = field.tempFolderPath || '';
      newInput.maxSelection = field.tempMaxSelection || 1;
      newInput.imagesAutoImport =
        field.tempImagesAutoImport !== undefined
          ? field.tempImagesAutoImport
          : true;
    }

    if (field.tempInputType === 'datepicker') {
      newInput.dateMinValue = 'today';
    }

    // Handle diacritics for text inputs
    let updatedDiacritics = field.diacritics || [];
    if (field.tempInputType === 'text' && field.tempDiacritic) {
      if (!updatedDiacritics.includes(newInput.name)) {
        updatedDiacritics = [...updatedDiacritics, newInput.name];
      }
    }

    // Handle autoCorrect for textarea inputs
    let updatedAutoCorrect = field.autoCorrect || [];
    if (field.tempInputType === 'textarea' && field.tempAutoCorrect) {
      if (!updatedAutoCorrect.includes(newInput.name)) {
        updatedAutoCorrect = [...updatedAutoCorrect, newInput.name];
      }
    }

    setField({
      ...field,
      inputs: [...(field.inputs || []), newInput],
      value: updatedValues,
      diacritics: updatedDiacritics,
      autoCorrect: updatedAutoCorrect,
      tempInputType: undefined,
      tempInputName: undefined,
      tempInputValues: [],
      tempAvailableImages: [],
      tempMaxSelection: 1,
      tempImagesAutoImport: undefined,
      tempInputLabel: undefined,
      tempImageName: undefined,
      tempImageLink: undefined,
      tempDiacritic: undefined,
      tempAutoCorrect: undefined,
    });
  };

  return (
    <Stack gap="md">
      <BasicFieldInputs
        fields={['label']}
        fieldValues={field}
        requiredFields={['label']}
        onChange={(key, value) => setField({ ...field, [key]: value })}
      />

      <Select
        label={intl.formatMessage({ id: 'inputType' })}
        name="inputType"
        placeholder={`${intl.formatMessage({ id: 'select' })}...`}
        value={field.tempInputType || null}
        onChange={(value) =>
          setField({ ...field, tempInputType: value as InputType })
        }
        data={[
          'text',
          'textarea',
          'number',
          'email',
          'tel',
          'datepicker',
          'picker',
          'checkboxGroup',
          'imagePicker',
          'signature',
        ]
          .sort()
          .map((item) => ({
            value: item,
            label: intl.formatMessage({ id: item }),
          }))}
      />

      <CustomInput
        type="text"
        value={field.tempInputLabel || ''}
        name="inputLabel"
        label={intl.formatMessage({ id: 'inputLabel' })}
        onChange={(value) =>
          setField({
            ...field,
            tempInputLabel: value,
            tempInputName: toInputName(value),
          })
        }
      />

      {field.tempInputType === 'picker' && (
        <PickerOptionsConfig
          items={field.tempInputValues || []}
          inputLabel={intl.formatMessage({ id: 'optionLabel' })}
          inputValue={field.tempPickerValue || ''}
          onInputChange={(value) =>
            setField({ ...field, tempPickerValue: value })
          }
          onAddItem={() => {
            if (field.tempPickerValue) {
              setField({
                ...field,
                tempInputValues: [
                  ...(field.tempInputValues || []),
                  {
                    id: field.tempPickerValue,
                    label: field.tempPickerValue,
                  },
                ],
                tempPickerValue: '',
              });
            }
          }}
          onRemoveItem={(index) => {
            setField({
              ...field,
              tempInputValues: (field.tempInputValues || []).filter(
                (_, i) => i !== index
              ),
            });
          }}
          addButtonDisabled={!field.tempPickerValue}
        />
      )}

      {field.tempInputType === 'checkboxGroup' && (
        <PickerOptionsConfig
          items={field.tempInputValues || []}
          inputLabel={intl.formatMessage({ id: 'optionLabel' })}
          inputValue={field.tempCheckboxValue || ''}
          onInputChange={(value) =>
            setField({ ...field, tempCheckboxValue: value })
          }
          onAddItem={() => {
            if (field.tempCheckboxValue) {
              setField({
                ...field,
                tempInputValues: [
                  ...(field.tempInputValues || []),
                  {
                    id: field.tempCheckboxValue,
                    label: field.tempCheckboxValue,
                  },
                ],
                tempCheckboxValue: '',
              });
            }
          }}
          onRemoveItem={(index) => {
            setField({
              ...field,
              tempInputValues: (field.tempInputValues || []).filter(
                (_, i) => i !== index
              ),
            });
          }}
          addButtonDisabled={!field.tempCheckboxValue}
        />
      )}

      {field.tempInputType === 'text' && (
        <Checkbox
          label={intl.formatMessage({ id: 'diacriticsFields' })}
          checked={!!field.tempDiacritic}
          onChange={(event) =>
            setField({
              ...field,
              tempDiacritic: event.currentTarget.checked
                ? field.tempInputName
                : undefined,
            })
          }
        />
      )}

      {field.tempInputType === 'textarea' && (
        <Checkbox
          label={intl.formatMessage({ id: 'autoCorrectFields' })}
          checked={!!field.tempAutoCorrect}
          onChange={(event) =>
            setField({
              ...field,
              tempAutoCorrect: event.currentTarget.checked
                ? field.tempInputName
                : undefined,
            })
          }
        />
      )}

      {field.tempInputType === 'imagePicker' && (
        <ImagePickerConfig
          maxSelection={field.tempMaxSelection || 1}
          availableImages={field.tempAvailableImages || []}
          imagesAutoImport={
            field.tempImagesAutoImport !== undefined
              ? field.tempImagesAutoImport
              : true
          }
          onMaxSelectionChange={(value) =>
            setField({ ...field, tempMaxSelection: value })
          }
          onImagesAutoImportChange={(value) =>
            setField({ ...field, tempImagesAutoImport: value })
          }
          onPhotosAdded={handlePhotosAdded}
          onRemoveImage={(index) => {
            setField({
              ...field,
              tempAvailableImages: (field.tempAvailableImages || []).filter(
                (_, i) => i !== index
              ),
            });
          }}
        />
      )}

      <Flex justify="flex-end">
        <Button
          id="infinite-list-add-entry-button"
          variant="default"
          onClick={handleAddInput}
        >
          {intl.formatMessage({ id: 'addEntry' })}
        </Button>
      </Flex>

      {field.inputs && field.inputs.length > 0 && (
        <Stack gap="md">
          {field.inputs.map((input, index) => (
            <InputListItem
              key={index}
              input={input}
              onRemove={() => handleRemoveInput(index)}
              onUpdate={(updatedInput) =>
                handleUpdateInput(index, updatedInput)
              }
              diacritics={field.diacritics}
              onDiacriticsChange={handleDiacriticsChange}
              autoCorrect={field.autoCorrect}
              onAutoCorrectChange={handleAutoCorrectChange}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
